<template>
  <div class="mobile-layout">
    <!-- 顶部导航栏 -->
    <div class="mobile-navbar">
      <div class="navbar-left">
        <el-button
          v-if="showBackButton"
          text
          class="back-btn"
          @click="handleBack"
        >
          <SvgIcon icon="ep:arrow-left" class="text-20px" />
        </el-button>
      </div>

      <div class="navbar-center">
        <h1 class="navbar-title">{{ pageTitle }}</h1>
      </div>

      <div class="navbar-right">
        <div class="lang-switch-wrapper">
          <LangSwitch class="lang-switch" />
        </div>
        <el-dropdown>
          <span class="user-menu">
            <SvgIcon icon="ep:more-filled" class="text-22px" />
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="handleLogout">
                <SvgIcon icon="ep:switch-button" class="mr-2" />
                {{ $t('common.logout') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="mobile-content">
      <router-view v-slot="{ Component }">
        <keep-alive :include="cachedViews">
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { usePageTitleStore } from '@/store/modules/pageTitle'
import { $t } from '@/locales'

defineOptions({
  name: 'MobileLayout'
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const pageTitleStore = usePageTitleStore()

// 页面标题
const pageTitle = computed(() => {
  // 优先使用store中的标题,如果没有则使用route.meta.title或默认标题
  if (pageTitleStore.currentTitle) {
    return t(`route.${pageTitleStore.currentTitle}`)
  }
  return (route.meta.title as string) || $t('system.title')
})

// 监听路由变化,自动更新页面标题
watch(
  () => route.name,
  (newRouteName) => {
    if (newRouteName && typeof newRouteName === 'string') {
      const name = newRouteName as string === 'root' ? 'query' : newRouteName as string
      pageTitleStore.setPageTitle(name)
    }
  },
  { immediate: true }
)

// 是否显示返回按钮
const showBackButton = computed(() => {
  // 在首页(root)和查询页不显示返回按钮
  return route.name !== 'root' && route.path !== '/query'
})

// 缓存的视图
const cachedViews = computed(() => {
  return ['QueryPage', 'CouponListPage']
})

// 返回上一页
const handleBack = () => {
  router.back()
}

// 退出登录
const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      $t('common.logoutConfirm'),
      $t('common.tips'),
      {
        confirmButtonText: $t('common.confirm'),
        cancelButtonText: $t('common.cancel'),
        type: 'warning'
      }
    )

    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    router.push('/login')
  } catch {
    // 用户取消
  }
}
</script>

<style scoped lang="scss">
.mobile-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  background-color: #fff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  
  // PC端显示优化
  @media (min-width: 769px) {
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.15);
  }
}

.mobile-navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.3);
  color: #333;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.navbar-left,
.navbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 80px;
}

.navbar-left {
  justify-content: flex-start;
}

.navbar-right {
  justify-content: flex-end;
}

.lang-switch-wrapper {
  :deep(.lang-switch) {
    // 适配浅色半透明背景
    .el-button {
      color: #333;
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
      }
      
      &:active,
      &:focus {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.back-btn {
  color: #333 !important;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.06) !important;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1) !important;
    transform: scale(1.05);
  }
  
  &:active,
  &:focus {
    background-color: rgba(0, 0, 0, 0.15) !important;
  }
  
  // 覆盖Element Plus的默认focus样式
  &.el-button:focus {
    background-color: rgba(0, 0, 0, 0.1) !important;
    color: #333 !important;
  }
  
  &.el-button:active {
    background-color: rgba(0, 0, 0, 0.15) !important;
    color: #333 !important;
  }
}

.navbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  overflow: hidden;
}

.navbar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #333;
  background-color: rgba(0, 0, 0, 0.06);
  transition: all 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }
  
  &:active {
    background-color: rgba(0, 0, 0, 0.15);
    transform: scale(0.98);
  }
}

.mobile-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f5f5f5;
  -webkit-overflow-scrolling: touch;
}
</style>
